import React, { useCallback, useEffect, useState } from "react";
import { platformAPI } from "../services/api";
import { telLink, whatsappLink } from "../utils/contact";

/**
 * Products owners could not find in the catalogue. Each is a gap to fill: add
 * the product to a template, then mark the request handled.
 */
export default function CatalogRequests() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await platformAPI.listCatalogRequests(status);
      setRows(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load requests");
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const [notice, setNotice] = useState("");

  // Handling it emails the owner (and whoever sent it) that it is dealt with;
  // the operator may add a few words. Cancelling the box leaves it open.
  const handle = async (row) => {
    const note = window.prompt("Add a word for the owner? It is sent with the email (optional).", "");
    if (note === null) return;

    try {
      const { data } = await platformAPI.handleCatalogRequest(row.id, note.trim() || undefined);
      setNotice(
        data.notified > 0
          ? `Marked handled. ${data.notified} email${data.notified > 1 ? "s" : ""} sent.`
          : "Marked handled. No email could be sent (no address, or mail is off).",
      );
    } catch (err) {
      setError(err.response?.data?.error || "Could not update this request");
    }
    await load();
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Product requests</h1>
      <p className="text-sm text-gray-500 mb-6">
        Things owners looked for in the starter catalogue and did not find.
      </p>

      <div className="flex gap-2 mb-4">
        {["open", "handled"].map((key) => (
          <button
            key={key}
            onClick={() => setStatus(key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${
              status === key ? "bg-primary-600 text-white" : "bg-white text-gray-600 shadow-sm"
            }`}
          >
            {key}
          </button>
        ))}
      </div>

      {notice && <p className="text-green-700 bg-green-50 rounded-lg px-3 py-2 mb-4 text-sm">{notice}</p>}
      {error && <p className="text-red-600 mb-4">{error}</p>}

      {rows.length === 0 ? (
        <p className="bg-white rounded-xl shadow px-4 py-10 text-center text-gray-400">Nothing here.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.id} className="bg-white rounded-xl shadow p-4 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-gray-900 whitespace-pre-wrap">{row.description}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {row.brand ? `${row.brand} · ` : ""}
                  {row.shop_name} · {row.requested_by} ·{" "}
                  {new Date(row.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {row.shop_phone && (
                  <>
                    <a href={telLink(row.shop_phone)} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm">
                      📞
                    </a>
                    <a
                      href={whatsappLink(row.shop_phone)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-green-100 text-green-800 text-sm"
                    >
                      💬
                    </a>
                  </>
                )}
                {row.status === "open" && (
                  <button onClick={() => handle(row)} className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold">
                    Mark handled
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
